import { IconProp, SizeProp } from "@fortawesome/fontawesome-svg-core";
import React from "react";
import styled from "styled-components";

import { Rounded } from "../theme/Rounded";
import { Spacing } from "../theme/Spacing";
import { ThemeType } from "../theme/Theme";
import { AtiraIcon } from "./AtiraIcon";
import { Flex } from "./Flex";
import { Spinner } from "./Spinner";
import { FontSize } from "./Text";
import { Tooltip } from "../utils/local-antd";

const IconWrapper = styled(Flex)<{ hasChildren: boolean; loading: boolean }>`
  justify-content: center;
  align-items: center;
  gap: ${({ hasChildren }) => (hasChildren ? Spacing.s : "0")};
  visibility: ${({ loading }) => (loading ? "hidden" : "visible")};
`;

const SpinnerWrapper = styled(Flex)`
  position: absolute;
  z-index: 1;
  inset: 0;
  border-radius: inherit;
  background-color: inherit;
  width: 100%;
  height: 100%;
  cursor: not-allowed;

  & > div {
    max-height: 2rem;
  }
`;

interface StyledButtonProps {
  color?: keyof ThemeType;
  backgroundColor?: keyof ThemeType;
  border?: string;
  align?: string;
  fontSize?: keyof typeof FontSize;
  width?: string;
  textAlign?: "start" | "center" | "end";
  padding?: keyof typeof Spacing;
  borderRadius?: keyof typeof Rounded;
  height?: string;
  margin?: keyof typeof Spacing;
  marginTop?: keyof typeof Spacing;
  hover?: ButtonProps["hover"];
  hoverColor?: ButtonProps["hoverColor"];
  hoverBackgroundColor?: ButtonProps["hoverBackgroundColor"];
  dataTestId?: string;
  loadingColor?: keyof ThemeType;
  $iconOnly?: boolean;
}

// @ts-ignore
const StyledButton = styled.button.attrs((props: StyledButtonProps) => ({
  "data-testid": props.dataTestId,
}))<StyledButtonProps>`
  color: ${(props) =>
    props.color ? props.theme[props.color] : props.theme["white"]};
  background-color: ${(props) =>
    props.backgroundColor
      ? props.theme[props.backgroundColor]
      : props.theme["main"]};
  padding: ${(props) =>
    props?.padding ? Spacing[props.padding] : `${Spacing.s} ${Spacing.l}`};
  border-radius: ${(props) => props.borderRadius || Rounded.md};
  border: ${(props) => props.border || "none"};
  margin: ${(props) =>
    props?.margin ? Spacing[props.margin] : `${Spacing.m} ${Spacing.none}`};
  text-align: ${(props) => props.align};
  font-size: ${(props) => FontSize[props.fontSize || "s"]};
  width: ${(props) => props.width};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  text-align: ${(props) => props.textAlign};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  height: ${(props) => props.height};
  transition: all 0.3s;
  font-family: inherit;
  user-select: none;
  position: relative;

  ${({ hover, theme, hoverColor, hoverBackgroundColor, $iconOnly }) => {
    if (!hover) {
      return "";
    }

    // Icon-only buttons are often styled transparent by their callers, where a
    // filled hover reads as a solid block behind the icon
    if ($iconOnly && !hoverBackgroundColor) {
      return `&:hover:not(:disabled) { opacity: 0.7; }`;
    }

    return `&:hover { background-color: ${theme[hoverBackgroundColor || "lightMain"]};
      color: ${theme[hoverColor || "white"]}
    }`;
  }};

  ${({ marginTop }) =>
    marginTop
      ? `
    margin-top: ${Spacing[marginTop]};
    `
      : ""}

  &:focus-visible {
    outline: none;
  }
`;

export interface ButtonProps {
  icon?: IconProp;
  color?: keyof ThemeType;
  backgroundColor?: keyof ThemeType;
  onClick?: (e?: React.MouseEvent) => void;
  children?: React.ReactNode;
  title?: string;
  border?: string;
  align?: string;
  fontSize?: keyof typeof FontSize;
  width?: string;
  height?: string;
  textAlign?: StyledButtonProps["textAlign"];
  padding?: keyof typeof Spacing;
  borderRadius?: keyof typeof Rounded;
  disabled?: boolean;
  loading?: boolean;
  margin?: keyof typeof Spacing;
  marginTop?: keyof typeof Spacing;
  style?: React.CSSProperties;
  hover?: boolean;
  hoverColor?: keyof ThemeType;
  iconWidth?: SizeProp;
  hoverBackgroundColor?: keyof ThemeType;
  dataTestId?: string;
  loadingColor?: keyof ThemeType;
  className?: string;
  info?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  loading,
  loadingColor,
  hover = true,
  ...props
}) => {
  const localOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.disabled || loading) {
      return;
    }

    props.onClick?.(e);
  };

  if (props.title) {
    return (
      <Tooltip title={props.info || ""} placement="top">
        <StyledButton {...props} onClick={localOnClick} hover={hover}>
          {props.title}
          {loading ? (
            <SpinnerWrapper>
              <Spinner color={loadingColor} />
            </SpinnerWrapper>
          ) : null}
        </StyledButton>
      </Tooltip>
    );
  }

  if (props.icon) {
    return (
      <Tooltip title={props.info || ""} placement="top">
        <StyledButton
          {...props}
          onClick={localOnClick}
          hover={hover}
          $iconOnly={!children}
        >
          <IconWrapper
            hasChildren={Boolean(children)}
            loading={Boolean(loading)}
          >
            <AtiraIcon icon={props.icon} size={props.iconWidth} />

            {children || props.title}
          </IconWrapper>

          {loading ? (
            <SpinnerWrapper>
              <Spinner color={loadingColor} />
            </SpinnerWrapper>
          ) : null}
        </StyledButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={props.info || ""} placement="top">
      <StyledButton {...props} onClick={localOnClick} hover={hover}>
        {children}
        {loading ? (
          <SpinnerWrapper>
            <Spinner color={loadingColor} />
          </SpinnerWrapper>
        ) : null}
      </StyledButton>
    </Tooltip>
  );
};
